import React, { useState, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaUserCircle } from 'react-icons/fa';
import { SlPower } from 'react-icons/sl';
import { useTranslation } from 'react-i18next';
import { logout, selectUser } from '../../store/slices/authSlice';



const ProfileButton = memo(({ onClick, isOpen, avatar, name }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center space-x-3
      p-2 rounded-lg
      hover:bg-green-50 dark:hover:bg-green-900/20
      transition-colors duration-200
      focus:outline-none focus:ring-2 focus:ring-green-500
      ${isOpen ? 'bg-green-50 dark:bg-green-900/20' : ''}
    `}
    aria-label="Profile Menu"
    aria-expanded={isOpen}
  >
    {avatar ? (
      <img
        src={avatar}
        alt={name}
        className="w-8 h-8 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
      />
    ) : (
      <FaUserCircle className="w-8 h-8 text-gray-600 dark:text-gray-300" />
    )}
  </button>
));

const DropdownMenu = memo(({ items, onItemClick }) => (
  <div className="
    absolute right-0 mt-2 w-56
    bg-white dark:bg-gray-800
    rounded-lg shadow-lg
    ring-1 ring-black ring-opacity-5
    transform opacity-100 scale-100
    transition-all duration-200 ease-out
    z-50
  ">
    <div className="p-2">
      {items.map(({ id, icon: Icon, translationKey, divider }) => (
        <React.Fragment key={id}>
          <button
            onClick={() => onItemClick(id)}
            className={`
              w-full px-4 py-2.5
              flex items-center space-x-3
              rounded-lg
              text-sm font-medium
              text-gray-700 dark:text-gray-200
              hover:bg-green-50 dark:hover:bg-green-900/20
              transition-colors duration-150
            `}
          >
            <Icon className="w-5 h-5" />
            <span>{translationKey}</span>
          </button>
          {divider && (
            <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
));

export const ProfileDropdown = memo(() => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // Redux'tan user bilgisini ve rolü alalım
  const user = useSelector(selectUser);

  const menuItems = React.useMemo(() => ([
    {
      id: 'logout',
      icon: SlPower,
      translationKey: t('profileDropdown.logout'),
      action: 'logout'
    }
  ]), [t]);
  const handleMenuItemClick = useCallback(async (id) => {
    const item = menuItems.find(item => item.id === id);
    if (item?.action === 'logout') {
      try {
        // Async logout thunk'ını dispatch et
        await dispatch(logout()).unwrap();
        // Login sayfasına yönlendir (correct path: /login not /auth/login)
        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Logout error:', error);
        // Hata durumunda da logout yap
        navigate('/login', { replace: true });
      }
    }
  }, [navigate, dispatch, menuItems]);
 
  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
 

  const handleClickOutside = useCallback((event) => {
    if (isOpen && !event.target.closest('.profile-dropdown')) {
      setIsOpen(false);
    }
  }, [isOpen]);

 

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return (
    <div className="relative profile-dropdown">
      <ProfileButton 
        onClick={handleToggle}
        isOpen={isOpen}
        avatar={user?.avatar}
        name={user?.name}
      />
      
      {isOpen && (
        <DropdownMenu
          items={menuItems}
          onItemClick={handleMenuItemClick}
        />
      )}
    </div>
  );
});

ProfileButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  avatar: PropTypes.string,
  name: PropTypes.string
};

DropdownMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      translationKey: PropTypes.string.isRequired
    })
  ).isRequired,
  onItemClick: PropTypes.func.isRequired
};

ProfileButton.displayName = 'ProfileButton';
DropdownMenu.displayName = 'DropdownMenu';
ProfileDropdown.displayName = 'ProfileDropdown';

export default ProfileDropdown;